import { api } from '../api/client';
import { store } from '../state/store';
import { WeekInfo, WeekNote } from '../types';
import { formatDisplayDate, formatISODate } from '../utils/date';
import { openEraModal } from './eras';

export interface WeekPreset {
  id: string;
  name: string;
  color: string;
  emoji: string;
  enjoyment: 'yes' | 'no';
  building: 'yes' | 'no';
  desc: string;
}

export const WEEK_PRESETS: WeekPreset[] = [
  {
    id: 'ideal',
    name: 'Ideal Week',
    color: '#22c55e',
    emoji: '🌟',
    enjoyment: 'yes',
    building: 'yes',
    desc: 'Enjoyed present & built for future',
  },
  {
    id: 'good-enjoyment',
    name: 'Good Enjoyment',
    color: '#eab308',
    emoji: '☀️',
    enjoyment: 'yes',
    building: 'no',
    desc: 'Fun & rest, harmless to future',
  },
  {
    id: 'good-building',
    name: 'Good Building',
    color: '#3b82f6',
    emoji: '🚀',
    enjoyment: 'no',
    building: 'yes',
    desc: 'Productive grind & future growth',
  },
  {
    id: 'unbalanced-fun',
    name: 'Short-term Fun',
    color: '#f97316',
    emoji: '⚡',
    enjoyment: 'yes',
    building: 'no',
    desc: 'Fun now at expense of future',
  },
  {
    id: 'unbalanced-grind',
    name: 'Unbalanced Grind',
    color: '#8b5cf6',
    emoji: '🌧️',
    enjoyment: 'no',
    building: 'no',
    desc: 'Tough struggle, stress or burnout',
  },
  {
    id: 'wasted',
    name: 'Unproductive',
    color: '#6b7280',
    emoji: '🌑',
    enjoyment: 'no',
    building: 'no',
    desc: 'Neither enjoyed nor built for future',
  },
];

export function initWeekModal(): void {
  const modal = document.getElementById('week-modal');
  const closeBtn = document.getElementById('modal-close-btn');

  closeBtn?.addEventListener('click', closeWeekModal);
  modal?.addEventListener('click', (e) => {
    if (e.target === modal) closeWeekModal();
  });

  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal && !modal.classList.contains('hidden')) {
      closeWeekModal();
    }
  });
}

export function openWeekModal(info: WeekInfo): void {
  const modal = document.getElementById('week-modal');
  const titleEl = document.getElementById('modal-week-title');
  const subtitleEl = document.getElementById('modal-week-subtitle');
  const bodyEl = document.getElementById('modal-week-body');

  if (!modal || !titleEl || !subtitleEl || !bodyEl) return;

  const startStr = formatDisplayDate(info.startDate);
  const endStr = formatDisplayDate(info.endDate);

  titleEl.textContent = `Age ${info.ageYears}, Week ${info.weekOfYear} (${info.index + 1} of ${store.getTotalWeeks()})`;
  subtitleEl.textContent = `${startStr} – ${endStr}`;

  bodyEl.innerHTML = '';

  // 1. Display active chapters/eras
  if (info.eras.length > 0) {
    const erasContainer = document.createElement('div');
    erasContainer.style.display = 'flex';
    erasContainer.style.flexWrap = 'wrap';
    erasContainer.style.gap = '0.5rem';
    erasContainer.style.marginBottom = '1rem';

    info.eras.forEach((era) => {
      const eraBadge = document.createElement('span');
      eraBadge.className = 'era-pill';
      eraBadge.style.borderColor = era.color;
      eraBadge.innerHTML = `<span class="era-color-dot" style="background:${era.color}"></span> <strong>${escapeHtml(era.title)}</strong>`;
      if (store.meta.isAdmin) {
        eraBadge.title = 'Click to edit this chapter';
        eraBadge.style.cursor = 'pointer';
        eraBadge.addEventListener('click', () => {
          closeWeekModal();
          openEraModal(era);
        });
      }
      erasContainer.appendChild(eraBadge);
    });

    bodyEl.appendChild(erasContainer);
  }

  // 2. If Admin: Show Form to Add/Edit Note
  if (store.meta.isAdmin) {
    renderAdminWeekEditor(bodyEl, info);
  } else {
    // Read-only Public View
    renderPublicWeekView(bodyEl, info);
  }

  modal.classList.remove('hidden');
}

export function closeWeekModal(): void {
  const modal = document.getElementById('week-modal');
  if (modal) modal.classList.add('hidden');
}

function renderPublicWeekView(container: HTMLElement, info: WeekInfo): void {
  const note = info.note;
  if (!note || (note.is_private && !store.meta.isAdmin)) {
    const empty = document.createElement('p');
    empty.className = 'text-muted';
    empty.style.fontStyle = 'italic';
    empty.textContent = 'No public notes or highlights shared for this week.';
    container.appendChild(empty);
    return;
  }

  if (note.color || note.assessment) {
    const matchedPreset = WEEK_PRESETS.find((p) => p.id === note.assessment || (note.color && p.color.toLowerCase() === note.color.toLowerCase()));
    const ratingBadge = document.createElement('div');
    ratingBadge.className = 'era-pill';
    if (note.color) ratingBadge.style.borderColor = note.color;
    ratingBadge.style.marginBottom = '0.75rem';
    ratingBadge.style.display = 'inline-flex';
    const dotColor = note.color || matchedPreset?.color || 'var(--accent)';
    const label = matchedPreset ? `${matchedPreset.emoji} ${matchedPreset.name}` : (note.assessment || 'Custom Week Rating');
    ratingBadge.innerHTML = `<span class="era-color-dot" style="background:${dotColor}"></span> <span>${escapeHtml(label)}</span>`;
    container.appendChild(ratingBadge);
  }

  if (note.title) {
    const heading = document.createElement('h4');
    heading.style.fontSize = '1.1rem';
    heading.style.fontWeight = '600';
    heading.textContent = note.title;
    container.appendChild(heading);
  }

  if (note.milestone_tag) {
    const tag = document.createElement('span');
    tag.className = 'badge-private';
    tag.style.backgroundColor = 'var(--accent-light)';
    tag.style.color = 'var(--accent)';
    tag.style.display = 'inline-block';
    tag.style.marginBottom = '0.5rem';
    tag.textContent = `🎯 ${note.milestone_tag}`;
    container.appendChild(tag);
  }

  if (note.content) {
    const content = document.createElement('div');
    content.style.whiteSpace = 'pre-wrap';
    content.style.lineHeight = '1.6';
    content.textContent = note.content;
    container.appendChild(content);
  }
}

function renderAdminWeekEditor(container: HTMLElement, info: WeekInfo): void {
  const note = info.note;
  let currentColor: string | null = note?.color || null;
  let currentAssessment: string | null = note?.assessment || null;

  const form = document.createElement('form');
  form.id = 'week-note-form';

  form.innerHTML = `
    <div class="form-group">
      <label for="week-note-title">Title / Milestone Summary</label>
      <input type="text" id="week-note-title" class="form-input" placeholder="e.g. Launched Project Alpha, Moved to Tokyo" value="${escapeHtml(note?.title || '')}" />
    </div>
    <div class="form-group">
      <label for="week-note-milestone">Milestone Tag (Optional)</label>
      <input type="text" id="week-note-milestone" class="form-input" placeholder="e.g. Career, Health, Travel, Launch" value="${escapeHtml(note?.milestone_tag || '')}" />
    </div>

    <!-- Questionnaire & Color Assessment Section -->
    <div class="questionnaire-card">
      <div class="questionnaire-header">
        <span class="questionnaire-title">The Contents of Your Week</span>
        <span class="text-muted" style="font-size:0.75rem;">Tim Urban's Week Assessment</span>
      </div>

      <div class="questionnaire-questions">
        <div class="q-block">
          <label class="q-label">1. Did you enjoy the week?</label>
          <div class="q-buttons" id="q-enjoyment-group">
            <button type="button" class="q-btn" data-val="yes">😊 Yes (Joy / Fun)</button>
            <button type="button" class="q-btn" data-val="no">😐 No (Tough / Unpleasant)</button>
          </div>
        </div>
        <div class="q-block">
          <label class="q-label">2. Did you build for the future or help others?</label>
          <div class="q-buttons" id="q-building-group">
            <button type="button" class="q-btn" data-val="yes">🚀 Yes (Growth / Value)</button>
            <button type="button" class="q-btn" data-val="no">⏳ No (Little lasting value)</button>
          </div>
        </div>
      </div>

      <div class="presets-section">
        <label class="q-label" style="margin-bottom: 0.35rem;">Quick Presets & Archetypes</label>
        <div class="presets-grid" id="presets-grid">
          ${WEEK_PRESETS.map((p) => `
            <button type="button" class="preset-card ${currentColor?.toLowerCase() === p.color.toLowerCase() || currentAssessment === p.id ? 'active' : ''}" data-color="${p.color}" data-id="${p.id}">
              <span class="preset-dot" style="background:${p.color}"></span>
              <span class="preset-name">${p.emoji} ${p.name}</span>
            </button>
          `).join('')}
        </div>
      </div>

      <div class="week-color-picker-row">
        <label for="week-note-color-input" class="q-label" style="margin: 0;">Week Color:</label>
        <div class="color-picker-wrapper">
          <input type="color" id="week-note-color-input" value="${currentColor || '#22c55e'}" />
          <span id="week-note-color-hex" class="color-hex-label">${currentColor ? currentColor : 'Default (Era/Passed)'}</span>
        </div>
        <button type="button" id="week-color-reset-btn" class="btn btn-sm btn-outline ${!currentColor ? 'hidden' : ''}" title="Clear custom color">↺ Reset</button>
      </div>
    </div>

    <div class="form-group">
      <label for="week-note-content">Weekly Journal / Reflection</label>
      <textarea id="week-note-content" class="form-input" rows="4" placeholder="Write thoughts, achievements, lessons learned...">${escapeHtml(note?.content || '')}</textarea>
    </div>
    <div class="form-group checkbox-group">
      <label>
        <input type="checkbox" id="week-note-private" ${note ? (note.is_private ? 'checked' : '') : 'checked'} />
        <span>Private Entry (Only visible when admin is logged in)</span>
      </label>
    </div>
    <div class="form-actions">
      ${note ? '<button type="button" id="week-note-delete-btn" class="btn btn-danger">Delete Note</button>' : ''}
      <div class="spacer"></div>
      <button type="button" id="week-note-cancel-btn" class="btn btn-outline">Cancel</button>
      <button type="submit" class="btn btn-primary">Save Note</button>
    </div>
  `;

  // Color picker and questionnaire controls
  const colorInput = form.querySelector('#week-note-color-input') as HTMLInputElement;
  const colorHex = form.querySelector('#week-note-color-hex') as HTMLElement;
  const resetBtn = form.querySelector('#week-color-reset-btn') as HTMLButtonElement;
  const presetBtns = form.querySelectorAll<HTMLButtonElement>('.preset-card');
  const enjoymentBtns = form.querySelectorAll<HTMLButtonElement>('#q-enjoyment-group .q-btn');
  const buildingBtns = form.querySelectorAll<HTMLButtonElement>('#q-building-group .q-btn');

  let selectedEnjoyment: 'yes' | 'no' | null = null;
  let selectedBuilding: 'yes' | 'no' | null = null;

  // Restore previous answers if an existing assessment or matching preset exists
  const existingPreset = WEEK_PRESETS.find((p) => p.id === currentAssessment || (currentColor && p.color.toLowerCase() === currentColor.toLowerCase()));
  if (existingPreset) {
    currentAssessment = existingPreset.id;
    selectedEnjoyment = existingPreset.enjoyment;
    selectedBuilding = existingPreset.building;
    enjoymentBtns.forEach((b) => b.classList.toggle('active', b.dataset.val === existingPreset.enjoyment));
    buildingBtns.forEach((b) => b.classList.toggle('active', b.dataset.val === existingPreset.building));
  }

  function updateColorState(color: string | null) {
    currentColor = color;
    if (color) {
      colorInput.value = color;
      colorHex.textContent = color;
      resetBtn.classList.remove('hidden');
    } else {
      colorHex.textContent = 'Default (Era/Passed)';
      resetBtn.classList.add('hidden');
    }

    // Highlight matching preset if any
    presetBtns.forEach((btn) => {
      const btnColor = btn.dataset.color?.toLowerCase();
      const btnId = btn.dataset.id;
      if ((color && btnColor === color.toLowerCase()) || (currentAssessment && btnId === currentAssessment)) {
        btn.classList.add('active');
      } else {
        btn.classList.remove('active');
      }
    });
  }

  function evaluateQuestionnaire() {
    if (!selectedEnjoyment || !selectedBuilding) return;

    // Find recommended preset based on questionnaire
    let recommended = WEEK_PRESETS[0]; // default ideal
    if (selectedEnjoyment === 'yes' && selectedBuilding === 'yes') {
      recommended = WEEK_PRESETS.find((p) => p.id === 'ideal') || WEEK_PRESETS[0];
    } else if (selectedEnjoyment === 'yes' && selectedBuilding === 'no') {
      recommended = WEEK_PRESETS.find((p) => p.id === 'good-enjoyment') || WEEK_PRESETS[1];
    } else if (selectedEnjoyment === 'no' && selectedBuilding === 'yes') {
      recommended = WEEK_PRESETS.find((p) => p.id === 'good-building') || WEEK_PRESETS[2];
    } else {
      recommended = WEEK_PRESETS.find((p) => p.id === 'wasted') || WEEK_PRESETS[5];
    }

    currentAssessment = recommended.id;
    updateColorState(recommended.color);
  }

  // Enjoyment question clicks
  enjoymentBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      enjoymentBtns.forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
      selectedEnjoyment = btn.dataset.val as 'yes' | 'no';
      evaluateQuestionnaire();
    });
  });

  // Building question clicks
  buildingBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      buildingBtns.forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
      selectedBuilding = btn.dataset.val as 'yes' | 'no';
      evaluateQuestionnaire();
    });
  });

  // Preset button clicks
  presetBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      const pColor = btn.dataset.color;
      const pId = btn.dataset.id;
      const preset = WEEK_PRESETS.find((p) => p.id === pId);
      if (pColor && preset) {
        currentAssessment = preset.id;
        // Sync questionnaire questions
        selectedEnjoyment = preset.enjoyment;
        selectedBuilding = preset.building;
        enjoymentBtns.forEach((b) => b.classList.toggle('active', b.dataset.val === preset.enjoyment));
        buildingBtns.forEach((b) => b.classList.toggle('active', b.dataset.val === preset.building));

        updateColorState(pColor);
      }
    });
  });

  // Manual Color Picker input
  colorInput.addEventListener('input', () => {
    const matched = WEEK_PRESETS.find((p) => p.color.toLowerCase() === colorInput.value.toLowerCase());
    currentAssessment = matched ? matched.id : 'custom';
    updateColorState(colorInput.value);
  });

  // Reset button click
  resetBtn.addEventListener('click', () => {
    selectedEnjoyment = null;
    selectedBuilding = null;
    currentAssessment = null;
    enjoymentBtns.forEach((b) => b.classList.remove('active'));
    buildingBtns.forEach((b) => b.classList.remove('active'));
    updateColorState(null);
  });

  // Submit handler
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const title = (form.querySelector('#week-note-title') as HTMLInputElement).value.trim();
    const milestone_tag = (form.querySelector('#week-note-milestone') as HTMLInputElement).value.trim();
    const content = (form.querySelector('#week-note-content') as HTMLTextAreaElement).value.trim();
    const is_private = (form.querySelector('#week-note-private') as HTMLInputElement).checked ? 1 : 0;

    const payload: WeekNote = {
      id: `week-${info.index}`,
      week_index: info.index,
      start_date: formatISODate(info.startDate),
      title: title || null,
      milestone_tag: milestone_tag || null,
      content: content || null,
      color: currentColor || null,
      assessment: currentAssessment || null,
      is_private,
    };

    try {
      const saved = await api.saveNote(payload);
      store.setNote(saved);
      closeWeekModal();
    } catch (err) {
      alert(`Failed to save note: ${String(err)}`);
    }
  });

  // Cancel handler
  form.querySelector('#week-note-cancel-btn')?.addEventListener('click', closeWeekModal);

  // Delete handler
  form.querySelector('#week-note-delete-btn')?.addEventListener('click', async () => {
    if (!note || !confirm('Are you sure you want to delete this week note?')) return;
    try {
      await api.deleteNote(note.id);
      store.removeNote(info.index);
      closeWeekModal();
    } catch (err) {
      alert(`Failed to delete note: ${String(err)}`);
    }
  });

  container.appendChild(form);
}

function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}


